import { useEffect, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import { fetchReportsData } from '../../../services/owner/financeApi';
import { peekOwnerCache } from '../../../services/cache/ownerCache';
import { durationOptions } from './reportsData';
import { usePlatformSettings } from '../../../context/PlatformSettingsContext';
import {
  exportSubscriptionReport,
  exportRevenueReport,
  exportAllReports,
  exportReportsCsv,
  exportReportsExcel,
  exportReportsPdf,
  printReports,
  printSubscriptionReport,
  printRevenueReport,
} from './exportReports';
import '../ownerPages.css';
import './reports.css';

const DURATION_MONTHS = {
  'This Month': 1,
  'Last 3 Months': 3,
  'Last 6 Months': 6,
  'This Year': 12,
};

function Reports() {
  const { formatPrice } = usePlatformSettings();
  const cached = peekOwnerCache('reports');
  const [search, setSearch] = useState('');
  const [durationFilter, setDurationFilter] = useState('all');
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [subscriptionReport, setSubscriptionReport] = useState(cached?.subscriptionReport || []);
  const [revenueReport, setRevenueReport] = useState(cached?.revenueReport || []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!peekOwnerCache('reports')) setLoading(true);
        setError('');
        const data = await fetchReportsData();
        if (cancelled) return;
        setSubscriptionReport(data.subscriptionReport);
        setRevenueReport(data.revenueReport);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rowMatches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (row) =>
      !query ||
      Object.values(row).some((value) => String(value).toLowerCase().includes(query));
  }, [search]);

  const sliceByDuration = (list) => {
    if (durationFilter === 'all') return list;
    const count = DURATION_MONTHS[durationFilter] || list.length;
    return list.slice(-count);
  };

  const filteredSubscription = subscriptionReport.filter(rowMatches);
  const filteredRevenue = sliceByDuration(revenueReport).filter(rowMatches);

  return (
    <div>
      <PageHeader title="Reports" subtitle="View and download platform reports" />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading reports...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-member-toolbar admin-list-toolbar">
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="owner-form-input rec-member-toolbar-select"
          value={durationFilter}
          onChange={(e) => setDurationFilter(e.target.value)}
          aria-label="Filter by duration"
        >
          <option value="all">All Duration</option>
          {durationOptions.map((duration) => (
            <option key={duration} value={duration}>{duration}</option>
          ))}
        </select>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printReports(filteredSubscription, filteredRevenue)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportsCsv(filteredSubscription, filteredRevenue)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportsPdf(filteredSubscription, filteredRevenue)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportReportsExcel(filteredSubscription, filteredRevenue)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportAllReports(filteredSubscription, filteredRevenue)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Subscription Report</h2>
          <div className="report-section-actions">
            <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printSubscriptionReport(filteredSubscription)}>
              Print
            </button>
            <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportSubscriptionReport(filteredSubscription)}>
              Download
            </button>
          </div>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table owner-panel-table">
            <thead>
              <tr><th>Plan</th><th>Active Count</th><th>Monthly</th><th>Quarterly</th><th>Yearly</th><th>Revenue</th></tr>
            </thead>
            <tbody>
              {filteredSubscription.length > 0 ? (
                filteredSubscription.map((row) => (
                  <tr key={row.plan}>
                    <td>{row.plan}</td><td>{row.activeCount}</td><td>{row.monthlyCount}</td><td>{row.quarterlyCount}</td><td>{row.yearlyCount}</td>
                    <td className="report-amount">{formatPrice(row.revenue)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No subscription data'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section report-section">
        <div className="report-section-header">
          <h2 className="owner-card-title">Revenue Report</h2>
          <div className="report-section-actions">
            <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printRevenueReport(filteredRevenue)}>
              Print
            </button>
            <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportRevenueReport(filteredRevenue)}>
              Download
            </button>
          </div>
        </div>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table owner-panel-table">
            <thead>
              <tr><th>Month</th><th>Subscriptions</th><th>Renewals</th><th>New Signups</th><th>Total</th></tr>
            </thead>
            <tbody>
              {filteredRevenue.length > 0 ? (
                filteredRevenue.map((row) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td className="report-amount">{formatPrice(row.subscriptions)}</td>
                    <td>{formatPrice(row.renewals)}</td>
                    <td>{formatPrice(row.newSignups)}</td>
                    <td className="report-amount">{formatPrice(row.total)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No revenue data'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Reports;
